import axios from "axios";
import { useEffect, useState } from "react";
import "./App.css";
import CustomizedTimeline from "./Components/Timeline";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import Banner from "./Components/Banner";
import ReleaseFilters from "./Components/ReleaseFilters";
import { Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const App = () => {
  const [error, setError] = useState(null);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [app, setApp] = useState("Imagine");
  const [typeFilter, setTypeFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [availableTypes, setAvailableTypes] = useState(["All"]);

  useEffect(() => {
    // Parse the URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    let appParam = urlParams.get("app");
    if (appParam === null) appParam = "Imagine";
    // Set the app state if the appParam exists and is not empty
    if (appParam && appParam.trim() !== "") {
      setApp(capitalizeFirstLetter(appParam.trim()));
      setReleaseNotes([]); // Reset the release notes to an empty array
      setCurrentPage(1); // Reset the current page to 1
    }
  }, []);

  useEffect(() => {
    if (app !== "") {
      const typeFilterQuery =
        typeFilter === "All"
          ? ""
          : `&filters[type][$eq]=${encodeURIComponent(typeFilter)}`;
      const normalizedSearchTerm = debouncedSearchTerm.trim();
      const searchFilterQuery =
        normalizedSearchTerm === ""
          ? ""
          : [
              ["title", normalizedSearchTerm],
              ["description", normalizedSearchTerm],
              ["jira_ticket_number", normalizedSearchTerm],
              ["type", normalizedSearchTerm],
            ]
              .map(
                ([field, value], index) =>
                  `&filters[$or][${index}][${field}][$containsi]=${encodeURIComponent(value)}`
              )
              .join("");

      // Only fetch release notes if app is not empty
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/api/release-notes?pagination[page]=${currentPage}&pagination[pageSize]=25&filters[application][$eq]=${encodeURIComponent(app)}${typeFilterQuery}${searchFilterQuery}&sort[0]=date%3Adesc`
        )
        .then(({ data }) => {
          setReleaseNotes((releaseNotes) =>
            currentPage === 1 ? data.data : [...releaseNotes, ...data.data]
          );
        })
        .catch((error) => setError(error));
    }
  }, [currentPage, app, typeFilter, debouncedSearchTerm]);

  useEffect(() => {
    const fetchReleaseTypes = async () => {
      const types = new Set();
      let page = 1;
      let pageCount = 1;

      do {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/release-notes?fields[0]=type&pagination[page]=${page}&pagination[pageSize]=100&filters[application][$eq]=${encodeURIComponent(app)}`
        );

        data.data.forEach(({ attributes }) => {
          if (attributes.type) {
            types.add(attributes.type);
          }
        });

        pageCount = data.meta?.pagination?.pageCount || 1;
        page += 1;
      } while (page <= pageCount);

      setAvailableTypes(["All", ...Array.from(types).sort()]);
    };

    if (app !== "") {
      fetchReleaseTypes().catch((error) => setError(error));
    }
  }, [app]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setReleaseNotes([]);
    setCurrentPage(1);
    setTypeFilter("All");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    document.title = `Retalia Changelog`;
  }, [app]);

  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setReleaseNotes([]);
      setCurrentPage(1);
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [debouncedSearchTerm, searchTerm]);

  const handleScroll = () => {
    const scrollTop =
      (document.documentElement && document.documentElement.scrollTop) ||
      document.body.scrollTop;
    const scrollHeight =
      (document.documentElement && document.documentElement.scrollHeight) ||
      document.body.scrollHeight;
    const clientHeight =
      document.documentElement.clientHeight || window.innerHeight;
    const scrollThreshold = 200; // Set a threshold value of 200 pixels from the bottom of the page
    const scrolledToBottom =
      Math.ceil(scrollTop + clientHeight + scrollThreshold) >= scrollHeight;
    if (scrolledToBottom) {
      setCurrentPage((currentPage) => currentPage + 1);
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleTypeChange = (selectedType) => {
    setReleaseNotes([]);
    setCurrentPage(1);
    setTypeFilter(selectedType);
  };

  if (error) {
    return <div>An error occured: {error.message}</div>;
  }

  return (
    <div className="App">
      <Header app={app} />
      <main className="release-notes-shell">
        <Banner app={app} />
        <ReleaseFilters
          availableTypes={availableTypes}
          typeFilter={typeFilter}
          searchTerm={searchTerm}
          onTypeChange={handleTypeChange}
          onSearchChange={setSearchTerm}
        />
        <section className="release-notes-panel" aria-label="Release notes">
          {releaseNotes.length > 0 ? (
            <CustomizedTimeline releaseNotes={releaseNotes} />
          ) : (
            <Typography className="no-notes-found">
              {typeFilter !== "All" || debouncedSearchTerm.trim() !== ""
                ? "No release notes match the selected filters"
                : `No release notes found for ${app}`}
            </Typography>
          )}
        </section>
      </main>
      {currentPage > 1 && (
        <button
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <ArrowUpwardIcon />
        </button>
      )}
      <Footer />
    </div>
  );
};

export default App;
